/**
 * Auth context — wraps Supabase Auth or Hive SSO (VITE_HIVE_URL set) behind a
 * provider-neutral `AppSession`. Exposes `useAuth()` with the current session,
 * profile, and active-org info.
 *
 * Consumers only ever read session truthiness and `session.user.email` — the
 * AppSession shape is deliberately minimal so both providers map onto it.
 *
 * Active-org selection defers to localStorage `athena.activeOrg` for the
 * X-Org-Id header. `/api/me/permissions` is queried separately by the
 * components that need tier info.
 */
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  type HiveSession,
  getHiveSession,
  isHiveMode,
  scheduleProactiveRefresh,
  signOutHive,
  subscribeHiveSession,
} from './hive';
import { type Session, supabase } from './supabase';

export interface AppSession {
  user: { id: string; email?: string };
  provider: 'supabase' | 'hive';
}

interface AuthState {
  session: AppSession | null;
  loading: boolean;
  activeOrgId: string | null;
  setActiveOrgId: (id: string | null) => void;
  signOut: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);
const ACTIVE_ORG_KEY = 'athena.activeOrg';

function fromSupabase(s: Session | null): AppSession | null {
  if (!s) return null;
  return { user: { id: s.user.id, email: s.user.email }, provider: 'supabase' };
}

function fromHive(s: HiveSession | null): AppSession | null {
  if (!s) return null;
  return { user: { id: s.user.id, email: s.user.email }, provider: 'hive' };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AppSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : window.localStorage.getItem(ACTIVE_ORG_KEY),
  );

  useEffect(() => {
    if (isHiveMode()) {
      setSession(fromHive(getHiveSession()));
      setLoading(false);
      scheduleProactiveRefresh();
      // Covers login/refresh/sign-out in this tab AND cross-tab rotation
      // adoption (Hive rotates refresh tokens; the storage event syncs tabs).
      return subscribeHiveSession((s) => setSession(fromHive(s)));
    }

    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) {
        setSession(fromSupabase(data.session));
        setLoading(false);
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) =>
      setSession(fromSupabase(s)),
    );
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const setActiveOrgId = useCallback((id: string | null) => {
    setActiveOrgIdState(id);
    if (id) window.localStorage.setItem(ACTIVE_ORG_KEY, id);
    else window.localStorage.removeItem(ACTIVE_ORG_KEY);
  }, []);

  const signOut = useCallback(async () => {
    if (isHiveMode()) {
      setActiveOrgId(null);
      signOutHive(); // clears local session + redirects to Hive end-session
      return;
    }
    await supabase.auth.signOut();
    setActiveOrgId(null);
  }, [setActiveOrgId]);

  const value = useMemo<AuthState>(
    () => ({ session, loading, activeOrgId, setActiveOrgId, signOut }),
    [session, loading, activeOrgId, setActiveOrgId, signOut],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('useAuth must be used within AuthProvider');
  return v;
}
